import { Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { provideTranslocoScope } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { BillingAddress, BusinessModel, MARKETPLACE_MATOMO_EVENT_CATEGORY_BILLING, MarketplaceBillingInformationState } from '@platform-frontend/marketplace';
import { TypedChanges } from '@platform-frontend/util';
import { MatomoTracker } from 'ngx-matomo-client';
import { tap } from 'rxjs/operators';
import { CHECKOUT_TRANSLOCO_SCOPE } from '../../checkout-transloco-scope';

interface BillingAddressForm {
  id: FormControl<number>;
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  organizationName: FormControl<string | null>;
  address: FormControl<string | null>;
  address2: FormControl<string | null>;
  postalCode: FormControl<string | null>;
  city: FormControl<string | null>;
  province: FormControl<string | null>;
  country: FormControl<string>;
  vatId: FormControl<string | null>;
  email: FormControl<string | null>;
  contractIdsForConnectedSubscriptions: FormControl<string[]>;
  serversideVersion: FormControl<string | null>;
}

@UntilDestroy()
@Component({
  selector: 'yat-billing-address-form',
  templateUrl: './billing-address-form.component.html',
  styleUrls: ['./billing-address-form.component.scss'],
  providers: [provideTranslocoScope(CHECKOUT_TRANSLOCO_SCOPE)]
})
export class BillingAddressFormComponent implements OnChanges, OnInit {
  constructor(private store: Store, private matomoTracker: MatomoTracker) {}

  @Input()
  editedBillingAddress: Partial<BillingAddress>;

  @Input()
  countries: string[] = [];

  @Input()
  buttonsClassName: string = '';

  @Input()
  businessModel: BusinessModel = 'B2B';

  @Output()
  changed: EventEmitter<BillingAddress> = new EventEmitter();

  @Output()
  save: EventEmitter<BillingAddress> = new EventEmitter();

  @Output()
  cancelMethod: EventEmitter<void> = new EventEmitter();

  billingAddressForm = new FormGroup<BillingAddressForm>({
    id: new FormControl(0, { nonNullable: true }),
    firstName: new FormControl(null, { nonNullable: true }),
    lastName: new FormControl(null, { nonNullable: true }),
    organizationName: new FormControl(null, { nonNullable: true, validators: Validators.required }),
    address: new FormControl(null, { nonNullable: true, validators: Validators.required }),
    address2: new FormControl(null, { nonNullable: true }),
    postalCode: new FormControl(null, { nonNullable: true, validators: Validators.required }),
    city: new FormControl(null, { nonNullable: true, validators: Validators.required }),
    province: new FormControl(null, { nonNullable: true }),
    country: new FormControl('', { nonNullable: true, validators: Validators.required }),
    vatId: new FormControl(null, { nonNullable: true }),
    email: new FormControl(null, { nonNullable: true, validators: Validators.email }),
    contractIdsForConnectedSubscriptions: new FormControl([], { nonNullable: true }),
    serversideVersion: new FormControl(null, { nonNullable: true })
  });

  private nameMode: 'individual' | 'organization' | 'both' = 'organization';
  readonly MATOMO_EVENT_CATEGORY_BILLING = MARKETPLACE_MATOMO_EVENT_CATEGORY_BILLING;

  ngOnInit(): void {
    this.billingAddressForm.valueChanges
      .pipe(
        tap(value => {
          this.setNameValidators(value.firstName ?? null, value.lastName ?? null, value.organizationName ?? null);
          this.changed.emit({
            id: value.id ?? 0,
            firstName: value.firstName ?? null,
            lastName: value.lastName ?? null,
            organizationName: value.organizationName ?? null,
            address: value.address ?? null,
            address2: value.address2 ?? null,
            postalCode: value.postalCode ?? null,
            city: value.city ?? null,
            province: value.province ?? null,
            country: value.country ?? '',
            email: value.email ?? null,
            vatId: value.vatId ?? null,
            vatVerified: false,
            contractIdsForConnectedSubscriptions: value.contractIdsForConnectedSubscriptions ?? [],
            serversideVersion: value.serversideVersion ?? null,
            readOnly: this.editedBillingAddress.readOnly ?? false
          });
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  ngOnChanges(changes: TypedChanges<BillingAddressFormComponent>): void {
    if (changes.editedBillingAddress) {
      this.billingAddressForm.reset(
        {
          id: 0,
          contractIdsForConnectedSubscriptions: [],
          serversideVersion: null,
          ...this.editedBillingAddress
        },
        { emitEvent: false }
      );
      this.setNameValidators(
        this.editedBillingAddress.firstName ?? null,
        this.editedBillingAddress.lastName ?? null,
        this.editedBillingAddress.organizationName ?? null
      );
      if (!('id' in this.editedBillingAddress) && !('email' in this.editedBillingAddress)) {
        this.billingAddressForm.controls.email.setValue(this.store.selectSnapshot(MarketplaceBillingInformationState.accountingEmail));
      }
    }
  }

  private setNameValidators(firstName: string | null, lastName: string | null, organizationName: string | null): void {
    const isSet = (val: string | null): boolean => val !== null && val !== '';
    const { firstName: firstCtrl, lastName: lastCtrl, organizationName: orgCtrl } = this.billingAddressForm.controls;
    let newNameMode: 'individual' | 'organization' | 'both' = 'both';
    const nameSet = isSet(firstName) || isSet(lastName);

    if (this.businessModel === 'B2C') {
      newNameMode = !isSet(organizationName) ? 'individual' : nameSet ? 'both' : 'organization';
    } else {
      newNameMode = !nameSet ? 'organization' : isSet(organizationName) ? 'both' : 'individual';
    }

    if (this.nameMode !== newNameMode) {
      this.nameMode = newNameMode;

      if (this.nameMode === 'organization') {
        [firstCtrl, lastCtrl].forEach(ctrl => ctrl.clearValidators());
        orgCtrl.setValidators([Validators.required]);
      } else if (this.nameMode === 'individual') {
        [firstCtrl, lastCtrl].forEach(ctrl => ctrl.setValidators([Validators.required]));
        orgCtrl.clearValidators();
      } else {
        [firstCtrl, lastCtrl, orgCtrl].forEach(ctrl => ctrl.setValidators([Validators.required]));
      }

      [firstCtrl, lastCtrl, orgCtrl].forEach(ctrl => ctrl.updateValueAndValidity({ onlySelf: true }));
    }
  }

  submitForm(value: BillingAddress): void {
    const simplifiedBillingAddress = this.simplifyBillingAddress(value);
    this.save.next(simplifiedBillingAddress);
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_BILLING, 'SaveAndContinue', 'AddNewBillingAddressForm');
  }

  private simplifyBillingAddress(billingAddress: BillingAddress): BillingAddress {
    return {
      ...billingAddress,
      vatId: billingAddress.vatId ? billingAddress.vatId.replace(/\s/g, '') : null
    };
  }

  cancel(): void {
    this.cancelMethod.emit();
    this.matomoTracker.trackEvent(this.MATOMO_EVENT_CATEGORY_BILLING, 'Back', 'AddNewBillingAddressForm');
  }
}
